import React, { useState, useEffect } from "react";
import { Modal, Form, Input, InputNumber, Select, DatePicker, Typography, Flex, Button, Checkbox } from "antd";
import { SwapOutlined, FileTextOutlined, FileDoneOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { amountFormatter, amountParser } from "../../../utils/formatters";
import { PIPELINE_STAGES } from "./pipelineData";

const { Title, Text } = Typography;

const STAGE_ORDER = {
  QUALIFICATION: 1,
  NEEDS_ANALYSIS: 2,
  VALUE_PROPOSITION: 3,
  PROPOSAL_QUOTE: 4,
  NEGOTIATION_REVIEW: 5,
  CLOSED_WON: 6,
  CLOSED_LOST: 6,
};

function MoveStageModal({ visible, onClose, deal, targetStage, onConfirmMove, onGenerateQuotation, onGenerateProposal, onGenerateInvoice }) {
  const [form] = Form.useForm();
  const [docGenerated, setDocGenerated] = useState(false);

  const isBackward = deal && targetStage && STAGE_ORDER[targetStage] < STAGE_ORDER[deal.pipelineStage];

  const targetStageObj = PIPELINE_STAGES.find((s) => s.code === targetStage);

  useEffect(() => {
    if (visible && deal) {
      setDocGenerated(false);
      form.setFieldsValue({
        qualificationOutcome: deal.qualificationOutcome || "QUALIFIED",
        dealCloseAmount: deal.amount,
        finalAmount: deal.amount,
        quoteAmount: deal.amount,
        proposalSentDate: dayjs(),
        expectedCloseDate: dayjs().add(15, "day"),
        advancePaid: "NO",
        docConfirmed: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, deal?._id || deal?.id, targetStage]);

  const handleFinish = (values) => {
    const { qualificationOutcome, lostReason, backwardReason, ...restTransition } = values;
    onConfirmMove({
      dealId: deal._id,
      targetStage,
      qualificationOutcome: qualificationOutcome || (targetStage === "NEEDS_ANALYSIS" ? "QUALIFIED" : undefined),
      lostReason,
      backwardReason,
      transitionData: {
        ...restTransition,
        ...(qualificationOutcome ? { qualificationOutcome } : {}),
      },
      isBackward,
    });
    onClose();
  };

  if (!deal || !targetStage) return null;

  return (
    <Modal
      open={visible}
      destroyOnClose
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Confirm Move"
      title={
        <Flex align="center" gap={8}>
          <SwapOutlined style={{ color: "#2459d3" }} />
          <Title level={4} style={{ margin: 0 }}>
            Move to {targetStageObj?.label || targetStage}
          </Title>
          {isBackward && (
            <Text type="danger" style={{ fontSize: "0.78rem", fontWeight: 600 }}>
              (Moving Backward)
            </Text>
          )}
        </Flex>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFinish} style={{ marginTop: 16 }}>
        <Text strong style={{ fontSize: "0.85rem", color: "#64748b", display: "block", marginBottom: 12 }}>
          Deal: {deal.leadCompanyName} &bull; {deal.dealName}
        </Text>

        {/* Backward Reason */}
        {isBackward && (
          <Form.Item label="Reason for moving backward" name="backwardReason" rules={[{ required: true, message: "Please specify backward reason" }]}>
            <Input.TextArea rows={2} placeholder="Why is this deal moving to an earlier stage?" />
          </Form.Item>
        )}

        {/* Target Stage Specific Inputs */}
        {!isBackward && targetStage === "QUALIFICATION" && (
          <Form.Item label="Qualification Notes" name="qualificationNotes">
            <Input.TextArea rows={2} placeholder="Initial qualification remarks..." />
          </Form.Item>
        )}

        {!isBackward && targetStage === "NEEDS_ANALYSIS" && (
          <>
            <Form.Item label="Qualification Outcome" name="qualificationOutcome" rules={[{ required: true, message: "Please select qualification outcome" }]}>
              <Select
                options={[
                  { value: "QUALIFIED", label: "Qualified" },
                  { value: "NOT_QUALIFIED", label: "Not Qualified" },
                ]}
              />
            </Form.Item>
            <Form.Item label="Customer Need" name="customerNeed">
              <Input.TextArea rows={2} placeholder="What does the customer require?" />
            </Form.Item>
            <Form.Item label="Customer Pain Points" name="painPoint">
              <Input.TextArea rows={2} placeholder="Key customer challenges..." />
            </Form.Item>
          </>
        )}

        {!isBackward && targetStage === "VALUE_PROPOSITION" && (
          <>
            <Form.Item label="Proposed Solution" name="proposedSolution">
              <Input.TextArea rows={2} placeholder="How does DealVoice address their needs?" />
            </Form.Item>
            <Form.Item label="Key Customer Benefits" name="keyBenefits">
              <Input.TextArea rows={2} placeholder="Expected business ROI..." />
            </Form.Item>
          </>
        )}

        {!isBackward && targetStage === "PROPOSAL_QUOTE" && (
          <>
            <Form.Item label="Quotation Amount (₹)" name="quoteAmount" rules={[{ required: true, message: "Quote amount is required" }]}>
              <InputNumber min={0} placeholder="0" formatter={amountFormatter} parser={amountParser} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Proposal Sent Date" name="proposalSentDate" rules={[{ required: true, message: "Proposal date is required" }]}>
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <div style={{ display: "flex", gap: 12, margin: "14px 0 14px 0" }}>
              <Button
                type="dashed"
                icon={<FileTextOutlined />}
                style={{ flex: 1, borderColor: "#2563eb", color: "#2563eb", fontWeight: 600, height: 38 }}
                onClick={() => {
                  onClose();
                  if (onGenerateProposal) {
                    onGenerateProposal(deal, form.getFieldValue("quoteAmount") || deal.amount);
                  }
                }}
              >
                Generate Proposal
              </Button>
              <Button
                type="dashed"
                icon={<FileTextOutlined />}
                style={{ flex: 1, borderColor: "#2563eb", color: "#2563eb", fontWeight: 600, height: 38 }}
                onClick={() => {
                  onClose();
                  if (onGenerateQuotation) {
                    onGenerateQuotation(deal, form.getFieldValue("quoteAmount") || deal.amount);
                  }
                }}
              >
                Generate Quotation
              </Button>
            </div>

            <Form.Item
              name="docConfirmed"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) => (value || docGenerated ? Promise.resolve() : Promise.reject(new Error("Please generate or confirm Quotation / Proposal before moving stage"))),
                },
              ]}
            >
              <Checkbox onChange={(e) => setDocGenerated(e.target.checked)}>Quotation or Proposal has been generated & shared with client</Checkbox>
            </Form.Item>
          </>
        )}

        {!isBackward && targetStage === "NEGOTIATION_REVIEW" && (
          <>
            <Form.Item label="Negotiated Deal Amount (₹)" name="dealCloseAmount">
              <InputNumber min={0} placeholder="0" formatter={amountFormatter} parser={amountParser} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Advance Payment Received?" name="advancePaid">
              <Select
                options={[
                  { value: "YES", label: "Yes - Advance Received" },
                  { value: "NO", label: "No - Pending" },
                ]}
              />
            </Form.Item>
            <Form.Item label="Negotiation Notes" name="negotiationNotes">
              <Input.TextArea rows={2} placeholder="Client payment terms or discount requests..." />
            </Form.Item>
            <Form.Item label="Expected Close Date" name="expectedCloseDate">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <div style={{ margin: "14px 0 8px 0" }}>
              <Button
                type="dashed"
                icon={<FileDoneOutlined />}
                style={{ width: "100%", borderColor: "#2563eb", color: "#2563eb", fontWeight: 600, height: 38 }}
                onClick={() => {
                  onClose();
                  if (onGenerateInvoice) {
                    onGenerateInvoice(deal, form.getFieldValue("dealCloseAmount") || deal.amount);
                  }
                }}
              >
                Generate Invoice
              </Button>
            </div>
          </>
        )}

        {!isBackward && targetStage === "CLOSED_WON" && (
          <>
            <Form.Item label="Final Won Amount (₹)" name="finalAmount" rules={[{ required: true }]}>
              <InputNumber min={0} formatter={amountFormatter} parser={amountParser} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item label="Won Success Notes" name="wonNotes">
              <Input.TextArea rows={2} placeholder="Why did we win this deal?" />
            </Form.Item>
          </>
        )}

        {!isBackward && targetStage === "CLOSED_LOST" && (
          <>
            <Form.Item label="Lost Reason" name="lostReason" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: "PRICE", label: "Pricing / Budget Mismatch" },
                  { value: "WRONG_TIME", label: "Wrong Timing / Postponed" },
                  { value: "COMPETITION", label: "Lost to Competitor" },
                ]}
              />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.lostReason !== cur.lostReason}>
              {({ getFieldValue }) =>
                getFieldValue("lostReason") === "COMPETITION" ? (
                  <Form.Item label="Competitor Name" name="competitor">
                    <Input placeholder="Who won the contract?" />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
            <Form.Item label="Loss Analysis Notes" name="lossNotes">
              <Input.TextArea rows={2} placeholder="Additional loss details..." />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}

export default MoveStageModal;
